import { AssociationCreate } from "@boark/shared/types/association-create";
import { AssociationUpdate } from "@boark/shared/types/association-update";
import {
  AssociationWithMembers,
  associationWithMembersSchema,
} from "@boark/shared/types/association-with-members";
import * as z from "zod";

import { request } from "../utils/request";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

async function parseError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => undefined)) as
    | { message?: string }
    | undefined;
  return new Error(body?.message ?? "Request failed");
}

export async function getAssociations(
  language: string,
): Promise<AssociationWithMembers[]> {
  return await request(`${API_BASE_URL}/association`, {
    schema: z.array(associationWithMembersSchema),
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": language,
    },
  });
}

export async function getAssociation(
  language: string,
  associationId: string,
): Promise<AssociationWithMembers> {
  return await request(`${API_BASE_URL}/association/${associationId}`, {
    schema: associationWithMembersSchema,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": language,
    },
  });
}

export async function createOwnedAssociation(
  input: AssociationCreate,
  language: string,
): Promise<AssociationWithMembers> {
  const response = await fetch(`${API_BASE_URL}/association`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": language,
    },
    credentials: "include",
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return associationWithMembersSchema.parse(await response.json());
}

export async function updateOwnedAssociation(
  id: string,
  input: AssociationUpdate,
  language: string,
): Promise<AssociationWithMembers> {
  const response = await fetch(`${API_BASE_URL}/association/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": language,
    },
    credentials: "include",
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return associationWithMembersSchema.parse(await response.json());
}
